import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Dropdown, message, Spin } from 'antd'
import type { MenuProps } from 'antd'
import TcccIcon from './TcccIcon'
import { t3cStore } from '@store/t3c-store'
import { useShallow } from 'zustand/shallow'

// T3C 坐席状态类型（官方文档定义的 5 种状态）
type T3CAgentStatus = 'free' | 'busy' | 'arrange' | 'notReady' | 'rest'

// 本地扩展状态（用于表示未登录/下线状态）
type LocalStatus = T3CAgentStatus | 'offline'

// 状态配置
interface StatusOption {
  key: LocalStatus
  label: string
  color: string
}

// 上线后可切换的状态选项
const ONLINE_STATUS_OPTIONS: StatusOption[] = [
  { key: 'free', label: '空闲', color: '#52c41a' },
  { key: 'notReady', label: '示忙', color: '#faad14' },
  // TODO: 暂时禁用下线功能，多Tab页存在问题
  // { key: 'offline', label: '下线', color: '#999999' },
]

// 下线状态时的选项
const OFFLINE_STATUS_OPTIONS: StatusOption[] = [
  // TODO: 暂时禁用上线功能，多Tab页存在问题
  { key: 'online' as LocalStatus, label: '上线', color: '#52c41a' },
  { key: 'offline', label: '下线', color: '#999999' },
]

// 所有状态的显示信息映射
const STATUS_INFO_MAP: Record<LocalStatus, { label: string; color: string }> = {
  free: { label: '空闲', color: '#52c41a' },
  busy: { label: '忙碌', color: '#ff4d4f' },
  arrange: { label: '话后整理', color: '#1890ff' },
  notReady: { label: '示忙', color: '#faad14' },
  rest: { label: '小休', color: '#722ed1' },
  offline: { label: '下线', color: '#999999' },
}

// 获取当前状态的显示信息
const getStatusInfo = (status: LocalStatus | undefined): { label: string; color: string } => {
  if (!status) {
    return STATUS_INFO_MAP.offline
  }
  return STATUS_INFO_MAP[status] || { label: '未知', color: '#999999' }
}

interface T3CStatusDropdownProps {
  style?: React.CSSProperties
}

const T3CStatusDropdown: React.FC<T3CStatusDropdownProps> = ({ style }) => {
  const [loading, setLoading] = useState(false)
  // 本地状态用于追踪当前坐席状态（初始默认为 free）
  const [localStatus, setLocalStatus] = useState<LocalStatus>('free')

  const { t3cReady, t3cSdkReady, reinitT3cSdk } = t3cStore(
    useShallow(s => ({
      t3cReady: s.t3cReady,
      t3cSdkReady: s.t3cSdkReady,
      reinitT3cSdk: s.reinitT3cSdk,
    }))
  )

  // 监听 T3C SDK 就绪状态，获取初始状态
  useEffect(() => {
    if (t3cReady && t3cSdkReady) {
      const status = window.tccc?.Agent?.getStatus() as T3CAgentStatus | undefined
      console.log('初始status:', status);

      // SDK 就绪后，如果获取到状态则使用，否则保持当前状态（默认 free）
      if (status) {
        setLocalStatus(status)
      }

      // 监听状态变化事件
      const handleStatusChanged = (res: { status: T3CAgentStatus }) => {
        console.log('状态切换status:', res);
        setLocalStatus(res.status)
      }

      window.tccc?.on?.('statusChanged', handleStatusChanged)

      return () => {
        window.tccc?.off?.('statusChanged', handleStatusChanged)
      }
    }
    // 移除 else 分支，不再在 SDK 未就绪时重置状态为 offline
  }, [t3cReady, t3cSdkReady])

  const currentStatus = localStatus

  const currentStatusInfo = useMemo(() => getStatusInfo(currentStatus), [currentStatus])

  // 切换状态
  const handleStatusChange = useCallback(async (targetStatus: string) => {
    if (!t3cReady || !t3cSdkReady) {
      message.warning('T3C 服务未就绪，请稍后再试')
      return
    }

    const agentStatus = window.tccc?.Agent?.getStatus() as T3CAgentStatus | undefined

    // 如果当前状态是忙碌或话后整理，不允许切换
    if (agentStatus === 'busy') {
      message.warning('当前正在通话中或话后整理，无法切换状态')
      return
    }

    // 如果选择的状态和当前状态相同，不做处理
    if (targetStatus === agentStatus || (targetStatus === 'offline' && localStatus === 'offline')) {
      return
    }

    setLoading(true)
    try {
      if (targetStatus === 'online') {
        // 上线：调用 online 方法
        window.tccc?.Agent?.online?.()
        // 上线后默认状态为 free
        setLocalStatus('free')
        message.success('已上线')
      } else if (targetStatus === 'offline') {
        // 下线：调用 offline 方法
        const result = await window.tccc?.Agent?.offline?.()
        console.log('切换为下线', result);

        if (result?.status === 'error') {
          message.error(`下线失败: ${result.errorMsg || '未知错误'}`)
        } else {
          message.success('已下线')
          setLocalStatus('offline')
        }
      } else {
        // 设置状态（free / notReady / rest）
        const result = await window.tccc?.Agent?.setStatus?.({ status: targetStatus as 'free' | 'notReady' | 'rest' })
        if (result?.status === 'error') {
          message.error(`状态切换失败: ${result.errorMsg || '未知错误'}`)
        } else {
          setLocalStatus(targetStatus as LocalStatus)
          const statusLabel = ONLINE_STATUS_OPTIONS.find(opt => opt.key === targetStatus)?.label || targetStatus
          message.success(`已切换为${statusLabel}`)
        }
      }
    } catch (error: any) {
      message.error(`状态切换失败: ${error?.message || '未知错误'}`)
      console.error('T3C 状态切换失败:', error)
    } finally {
      setLoading(false)
    }
  }, [t3cReady, t3cSdkReady, localStatus])

  // 根据当前状态选择对应的下拉选项
  const currentOptions = localStatus === 'offline' ? OFFLINE_STATUS_OPTIONS : ONLINE_STATUS_OPTIONS

  // 下拉菜单项
  const menuItems: MenuProps['items'] = currentOptions.map(option => ({
    key: option.key,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: option.color,
          }}
        />
        <span>{option.label}</span>
        {currentStatus === option.key && (
          <span style={{ marginLeft: 'auto', color: '#1890ff', fontSize: '12px' }}>当前</span>
        )}
      </div>
    ),
    disabled: currentStatus === option.key || currentStatus === 'busy' || currentStatus === 'arrange',
    onClick: () => handleStatusChange(option.key),
  }))

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomLeft"
      disabled={loading}
    >
      <div style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', ...style }}>
        <Spin spinning={loading} size="small">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <TcccIcon />
            {/* 状态颜色指示点 */}
            <span
              style={{
                position: 'absolute',
                right: '0',
                bottom: '10px',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: currentStatusInfo.color,
                border: '2px solid #fff',
                boxShadow: '0 0 2px rgba(0, 0, 0, 0.2)',
              }}
            />
          </div>
        </Spin>
        <span
          style={{
            fontSize: '12px',
            color: currentStatusInfo.color,
            marginTop: '4px',
          }}
        >
          {currentStatusInfo.label}
        </span>
      </div>
    </Dropdown>
  )
}

export default T3CStatusDropdown
